import { useState, useContext, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useQuery, useLazyQuery } from "@apollo/client"
import _ from "lodash"
import ParticipantProfileBoxSmall from "../../components/ParticipantProfileBoxSmall"
import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import StaffSidebar from "../../components/sidebars/StaffSidebar"
import StaffProfileBox from "../Staff/components/StaffProfileBox"
import { UserContext } from "../../context/userContext"
import { GET_TEMPLATE_VIEW_MODE, GET_BLOCKS_SAVED, GET_STAFF } from "./queries"
import FieldValuePerType from "./FieldValuePerType"
import GLOBALS_STATUS from "../../config/globalsStatus"
import { Button } from "primereact/button"
import { navigateWithOrigin } from "../../helpers/navigation"

export default function FormViewMode() {
  const [formValues, setFormValues] = useState({})
  const [titlePage, setTitle] = useState("")
  const [descriptionPage, setDescription] = useState("")
  const [isRefetching, setIsRefetching] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const referringLocation = location.state?.origin //If an origin is provided in state, use it to navigate on form completion
  const userCtx = useContext(UserContext)
  const { idTemplate, idStamp = null } = useParams()
  const PID = userCtx.participant_id || "not-set"
  const supervisionStaffMembershipID =
    userCtx.current_staff_membership_id || null
  const TYPE = "system"

  const [staffId, setStaffId] = useState("")
  const [staffEditId, setStaffEditId] = useState("")
  const [organizationId, setOrganizationId] = useState("")
  const [staffName, setStaffName] = useState("")
  const [staffAlias, setStaffAlias] = useState("")
  const [staffMembershipId, setStaffMembershipId] = useState("")
  const [staffEditMembershipId, setStaffEditMembershipId] = useState("")
  const [manageServiceType, setManageServiceType] = useState("")
  const [staffEditName, setStaffEditName] = useState("")
  const [staffEditAlias, setStaffEditAlias] = useState("")

  const [getBlocksSaved] = useLazyQuery(GET_BLOCKS_SAVED)
  const [getStaffName] = useLazyQuery(GET_STAFF)
  const [getStaffNameEdit] = useLazyQuery(GET_STAFF)

  const HIDE_TEMPLATES = [
    "intake_session",
    "discharge_session",
    "manage_services",
  ]
  const hidden = _.includes(HIDE_TEMPLATES, idTemplate)
  const isManageService = idTemplate === "manage_services"

  const { loading, data, refetch } = useQuery(GET_TEMPLATE_VIEW_MODE, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      idTemplate,
      type: TYPE,
      participant_id: PID,
      idStamp,
      retrieveLatestAssessment: true,
      supervision_staff_membership_id: supervisionStaffMembershipID,
    },
    skip: !userCtx.session.user.staff_id,
    onCompleted: (data) => {
      try {
        const parsedFormValues = JSON.parse(data.template.values) || {}
        const filterEmptyValues = _.reduce(
          parsedFormValues,
          (acc, value, key) => {
            if (key === "participant__manage_services") {
              setManageServiceType(value?.manage_services_type)
            }
            if (!_.isEmpty(value)) {
              return { ...acc, [key]: value }
            }
            return acc
          },
          {},
        )
        setTitle(data.template.template_title)
        setDescription(data.template.template_description)
        setFormValues(filterEmptyValues)
      } catch (error) {}
    },
  })

  const determineBlockName = () => {
    return idTemplate === "note_form" ? "form" : "session"
  }

  useEffect(() => {
    const handleRefetch = async () => {
      setIsRefetching(true)
      await refetch()
      setIsRefetching(false)
    }
    const getStaffId = async () => {
      await getBlocksSaved({
        variables: { idStamp },
        onCompleted: ({ getBlockSavedByIdStamp }) => {
          const parsedFormValues = JSON.parse(getBlockSavedByIdStamp.data)
          const blockNameToCheck = determineBlockName()
          parsedFormValues.forEach((block) => {
            if (
              block.block_name === blockNameToCheck ||
              block.block_name === "participant__manage_services"
            ) {
              if (
                !_.isEmpty(block.staff_id) &&
                !_.isEmpty(block.organization_id)
              ) {
                setStaffId(block.staff_id)
                setStaffAlias(block.staff_alias)
                setOrganizationId(block.organization_id)
              }
              if (
                !_.isEmpty(block.edited_by_staff_id) &&
                !_.isEmpty(block.organization_id)
              ) {
                setStaffEditId(block.edited_by_staff_id)
                setStaffEditAlias(block.edited_by_staff_alias)
                setOrganizationId(block.organization_id)
              }
            }
          })
        },
      })
    }

    const getStaffNameSaved = async () => {
      if (!staffId || !organizationId) return
      await getStaffName({
        variables: { staff_id: staffId, organization_id: organizationId },
        onCompleted: ({ user, userNameOnly }) => {
          if (user) {
            const name_first = user?.staff_name_first || ""
            const name_last = user?.staff_name_last || ""
            setStaffName(`${name_first} ${name_last}`)
            setStaffMembershipId(user?.staff_membership_id || "")
          } else if (userNameOnly) {
            setStaffName(userNameOnly.data)
            setStaffMembershipId("")
          } else {
            setStaffName("")
            setStaffMembershipId("")
          }
        },
      })
    }

    const getStaffEditNameSaved = async () => {
      if (!staffEditId || !organizationId) return
      await getStaffNameEdit({
        variables: { staff_id: staffEditId, organization_id: organizationId },
        onCompleted: ({ user, userNameOnly }) => {
          if (user) {
            const name_first = user?.staff_name_first || ""
            const name_last = user?.staff_name_last || ""
            setStaffEditName(`${name_first} ${name_last}`)
            setStaffEditMembershipId(user?.staff_membership_id || "")
          } else if (userNameOnly) {
            setStaffEditName(userNameOnly.data)
            setStaffEditMembershipId("")
          } else {
            setStaffEditName("")
            setStaffEditMembershipId("")
          }
        },
      })
    }

    getStaffId()
    getStaffNameSaved()
    getStaffEditNameSaved()
    handleRefetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getBlocksSaved,
    getStaffName,
    getStaffNameEdit,
    idStamp,
    organizationId,
    refetch,
    staffEditId,
    staffId,
  ])

  useEffect(() => {
    if (
      organizationId &&
      userCtx.session.user.organization_id !== organizationId
    ) {
      navigate(referringLocation ? referringLocation : -1)
    }
  }, [
    userCtx.session.user.organization_id,
    organizationId,
    navigate,
    referringLocation,
  ])

  const handleFieldsPerBlock = (fields, block_name, block_title) => {
    return fields.map(
      ({
        field_label,
        field_name,
        field_type,
        field_type_subtype,
        field_options,
        field_description,
        multi_fields,
      }) => {
        const blockValues = formValues[block_name]

        const essentialFieldNames = [
          "form__location_ids",
          "form_date",
          "form_channel",
          "form_start_time",
          "form_end_time",
          "demo_ssn",
          "activity__program_identity_ids",
          "session__location_ids",
          "session_date",
          "session_channel",
          "session_start_time",
          "session_length",
          "manage_services_type",
          "manage_staff_id_selector",
          "manage_program_identity_ids",
          "manage_location_ids",
          "assessments",
        ]

        let skipField =
          (field_type === "hidden" || field_type === "sys") &&
          !essentialFieldNames.includes(field_name)

        if (idTemplate === "manage_services") {
          const manageServiceTypeMappings = {
            "Staff assignment": [
              "manage_program_identity_ids",
              "manage_location_ids",
              "manage_external_id",
            ],
            "Program ID assignment": [
              "manage_staff_id_selector",
              "manage_location_ids",
              "manage_external_id",
            ],
            "Location assignment": [
              "manage_staff_id_selector",
              "manage_program_identity_ids",
              "manage_external_id",
            ],
            "External ID assignment": [
              "manage_staff_id_selector",
              "manage_program_identity_ids",
              "manage_location_ids",
            ],
            "Participant reactivation": [
              "manage_staff_id_selector",
              "manage_program_identity_ids",
              "manage_location_ids",
              "manage_external_id",
            ],
          }
          if (
            manageServiceTypeMappings[manageServiceType]?.includes(field_name)
          ) {
            skipField = true
          }
        }

        if (skipField) return null

        const fieldValuePerTypeArg = {
          blockValues,
          fieldName: field_name,
          fieldType: field_type,
          fieldSubtype: field_type_subtype,
          fieldOptions: field_options,
          description: field_description,
          multi_fields: multi_fields || null,
        }

        const handleLabel = () => {
          const labelMappings = {
            group_supervision__program_identity_ids: "Program IDs",
            activity__program_identity_ids: "Programs",
            session__location_ids: "Locations",
            group_supervision_topics_other_type: "Other topics",
            manage_services_date: "Service date",
            manage_services_type: "Service event",
            manage_notes: "Notes",
            manage_location_ids: "Assigned locations",
            manage_external_id: "External ID update",
            manage_staff_id_selector: "Assigned staff",
            manage_program_identity_ids: "Assigned programs",
            manage_organization_ids: "Shared organization",
          }
          return labelMappings[field_name] || field_label
        }

        return (
          <div id={field_name} key={field_name} className="view-data-field">
            <div className="field-name">
              <strong>{handleLabel()}</strong>
            </div>
            <div className="field-value">
              <FieldValuePerType
                fieldValuePerTypeArg={fieldValuePerTypeArg}
                block_title={block_title}
              />
            </div>
          </div>
        )
      },
    )
  }

  const getTitle = () => titlePage || ""

  const getDescription = () => {
    return idTemplate !== "note_form" ? descriptionPage || "" : ""
  }

  const handleDateFormat = (dateInput) => {
    if (dateInput === "not-set") {
      return ""
    }
    const date = new Date(dateInput)
    const month = date.getMonth() + 1
    const day = date.getDate()
    const year = date.getFullYear()
    return `${month}/${day}/${year}`
  }

  const handleBlocks = () => {
    const { blocks } = data.template
    const blocksOrderedByIndex = _.orderBy(blocks, ["index"])
    const blockNameToRender = determineBlockName()

    return blocksOrderedByIndex.map(({ block_title, block_name, fields }) => {
      if (!formValues[block_name]) return null
      const blockData = formValues[block_name]

      if (
        block_name === blockNameToRender ||
        block_name === "participant__manage_services"
      ) {
        return (
          <div key={block_name}>
            <div id="" className="view-data-field">
              <div className="field-name">
                <strong>Date completed</strong>
              </div>
              <div className="field-value">
                <div>
                  {blockData.date_created
                    ? handleDateFormat(blockData.date_created)
                    : ""}
                </div>
              </div>
            </div>
            <div id="" className="view-data-field">
              <div className="field-name">
                <strong>Performed by</strong>
              </div>
              <div className="field-value">
                <div>
                  {[staffName, staffMembershipId].filter(Boolean).join(" / ")}
                </div>
              </div>
            </div>
            <div id="" className="view-data-field">
              <div className="field-name">
                <strong>Last updated</strong>
              </div>
              <div className="field-value">
                <div>
                  {blockData.date_changed
                    ? handleDateFormat(blockData.date_changed)
                    : ""}
                </div>
              </div>
            </div>
            <div id="" className="view-data-field">
              <div className="field-name">
                <strong>Last updated by</strong>
              </div>
              <div className="field-value">
                <div>
                  {[staffEditName, staffEditMembershipId]
                    .filter(Boolean)
                    .join(" / ")}
                </div>
              </div>
            </div>
            <hr />
            <h2>{block_title}</h2>
            {handleFieldsPerBlock(fields, block_name, block_title)}
          </div>
        )
      } else {
        return (
          <div id={block_name} key={block_name}>
            <hr />
            <h2>{block_title}</h2>
            {handleFieldsPerBlock(fields, block_name, block_title)}
          </div>
        )
      }
    })
  }

  if (loading) return <div>Loading...</div>

  function getSidebarComponent(idTemplate) {
    switch (idTemplate) {
      case "individual_supervision_session":
      case "document_record_staff":
        return <StaffSidebar />
      case "group_supervision_session":
        return null
      default:
        if (
          _.includes(idTemplate, "group") ||
          _.includes(idTemplate, "log") ||
          _.includes(idTemplate, "individual") ||
          _.includes(idTemplate, "employer_contact")
        ) {
          return null
        }
        return <ParticipantSidebar isFormViewActive={true} />
    }
  }

  const isSidebarEmpty = getSidebarComponent(idTemplate) === null

  function getProfileBox(idTemplate, userCtx) {
    const isStaffProfile = [
      "individual_supervision_session",
      "document_record_staff",
    ].includes(idTemplate)

    if (isStaffProfile) {
      return <StaffProfileBox />
    }

    const hasParticipantData =
      !_.isEmpty(userCtx.participant_id) &&
      !_.isEmpty(userCtx.current_participant_id)

    return hasParticipantData ? <ParticipantProfileBoxSmall /> : null
  }

  return (
    <div
      className={`layout ${isSidebarEmpty ? "" : "layout-with-sidebar"}`}
      id="top-view-form"
    >
      {!isSidebarEmpty && (
        <div className="sidebar">{getSidebarComponent(idTemplate)}</div>
      )}

      <div className="main-content">
        <br />

        {getProfileBox(idTemplate, userCtx)}

        <div>
          <h1>{getTitle()}</h1>
          <div className="description-links-block">
            <div className="description-block">
              <p>{getDescription()}</p>
            </div>
            <div className="links-block">
              <Button
                className="btn-sm"
                onClick={() =>
                  navigate(referringLocation ? referringLocation : -1)
                }
              >
                Close View
              </Button>
              {!hidden &&
                _.includes(
                  GLOBALS_STATUS.ACTIVE_STATUS,
                  userCtx.participant_membership_status,
                ) && (
                  <Button
                    type="button"
                    className="btn-sm"
                    onClick={() => {
                      navigateWithOrigin(
                        navigate,
                        `/form/${idTemplate}/1/${idStamp}`,
                        { state: { returnOrigin: referringLocation } },
                      )
                    }}
                  >
                    Edit
                  </Button>
                )}
            </div>
          </div>

          {!isManageService && <hr style={{ marginTop: "20px" }} />}

          <div>{handleBlocks()}</div>
          <div>
            <a href="#top-view-form" className="btn-sm scroll-top">
              Back to Top
            </a>
          </div>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `#participant__outcomes .view-data-field{display:block}#participant__outcomes .field-value{display:flex}#participant__outcomes .outcomes-values{display:flex;width:100%}#participant__outcomes .outcomes-values span{flex:1;width:50%;box-sizing:border-box}#participant__outcomes .field-value div{background-color:transparent;padding-left:0}#participant__outcomes span.outcomes-value{background-color:rgb(242 242 242/var(--tw-bg-opacity));border-radius:5px;min-height:28px;padding:5px 5px 5px 15px}#participant__outcomes .field-name{font-size:18px} #participant__outcomes .view-data-field:has( .field-value:empty) {display: none;}`,
        }}
      />
    </div>
  )
}
